import { useCallback, useEffect, useRef, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { PaymentsWalletsService } from "../services/paymentsWallets.service";
import type { ProviderFloatsOverview } from "../types/payments-wallets.types";

export interface UseProviderFloatsResult {
  data: ProviderFloatsOverview | null;
  isLoading: boolean;
  error: string | null;
  /** False after a 401/403 on the read — the tab renders the no-access view. */
  hasAccess: boolean;
  /** Re-reads the overview (the server reuses readings younger than a minute). */
  reload: () => void;
  /** Asks every provider again now (`payments:write`). */
  readAgain: () => void;
  /** The last "read again" was refused for lack of `payments:write`; the read itself still works. */
  refreshDenied: boolean;
}

/**
 * Staff treasury floats (`GET /internal/payments/provider-balances/overview`). Nothing is asked until
 * `enabled` — every read can reach the providers — and then once per `reload`, `readAgain` or `reloadSignal`.
 * Loading is derived from the request the last answer settled, never set inside the effect.
 */
export function useProviderFloats(enabled: boolean, reloadSignal = 0): UseProviderFloatsResult {
  const [requestKey, setRequestKey] = useState(0);
  const [state, setState] = useState<{
    key: string | null;
    data: ProviderFloatsOverview | null;
    error: string | null;
  }>({ key: null, data: null, error: null });
  const [hasAccess, setHasAccess] = useState(true);
  const [refreshDenied, setRefreshDenied] = useState(false);
  const forceNext = useRef(false);
  const seq = useRef(0);
  const key = `${requestKey}|${reloadSignal}`;

  useEffect(() => {
    if (!enabled || !hasAccess) return;
    const current = ++seq.current;
    const force = forceNext.current;
    forceNext.current = false;
    const request = force
      ? PaymentsWalletsService.refreshProviderFloats()
      : PaymentsWalletsService.providerFloats();
    request
      .then((res) => {
        if (current !== seq.current) return;
        setRefreshDenied(false);
        setState({ key, data: res.data, error: null });
      })
      .catch((err: unknown) => {
        if (current !== seq.current) return;
        if (HttpApiService.isAccessDeniedError(err)) {
          // A refused refresh is a missing payments:write, not a missing read: keep the readings on screen.
          if (force) {
            setRefreshDenied(true);
            setState((previous) => ({ ...previous, key }));
          } else {
            setHasAccess(false);
          }
          return;
        }
        setState((previous) => ({ key, data: previous.data, error: HttpApiService.getErrorMessage(err) }));
      });
  }, [enabled, hasAccess, key]);

  const reload = useCallback(() => setRequestKey((value) => value + 1), []);
  const readAgain = useCallback(() => {
    forceNext.current = true;
    setRequestKey((value) => value + 1);
  }, []);

  return {
    data: state.data,
    isLoading: enabled && hasAccess && state.key !== key,
    error: state.error,
    hasAccess,
    reload,
    readAgain,
    refreshDenied,
  };
}
